<template>
  <div class="document-card-wrapper">
    <div class="document-card-wrapper-content">
      <div class="image-container">
        <BaseImage
          v-if="isImageValid && firstPageUrl"
          class="document-thumb"
          :src="firstPageUrl"
          alt="thumb"
          @load="handleImageLoad"
          @error="handleImageError"
        />
        <BbIcon v-else-if="!isImageValid && toDownload" name="translate" class="default-thumb" />
        <BbIcon v-else name="slides" class="default-thumb" />
        <div class="favorite-button-container">
          <BbBtn
            v-if="toDownload"
            size="large"
            type="icon"
            icon="download"
            icon-size="small"
            no-background
            icon-type="outlined"
            @click="clickButton"
          />
          <CollectionButton v-else :document="data" />
        </div>
      </div>
      <div class="document-details">
        <h4 :class="['document-title', !isTitleActive ? 'inactive-class' : '']" @click.stop="clickTitle">
          {{ computedTitle }}
        </h4>
        <div>
          <span>{{ data.pageCount }} pages</span>
          <span class="bullet">•</span>
          <span>{{ documentType }}</span>
        </div>
      </div>
    </div>
    <slot />
  </div>
</template>

<script>
import { computed, ref } from 'vue';

import CollectionButton from '~/components/common/CollectionButton';
import { addLanguageToFileName } from '~/utils/document-utils';
import { isPdfExtension, isTempDocument } from '~/utils/document-utils';
import validateUrl from '~/utils/url';
import BaseImage from '~/components/common/BaseImage.vue';

export default {
  methods: { addLanguageToFileName },
  props: {
    index: {
      type: Number,
      default: 0,
    },
    data: {
      type: Object,
      default: () => {},
    },
    documentType: {
      type: String,
      default: 'PDF',
    },
    toDownload: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BaseImage,
    CollectionButton,
  },
  setup(props, { emit }) {
    const isImageValid = ref(true);

    const firstPageUrl = computed(() =>
      validateUrl(props.data.pages?.[1]?.small) ? props.data.pages?.[1]?.small : '',
    );
    // consider that every source document (open source) is pdf
    const isTitleActive = computed(() =>
      isTempDocument(props.data.externalId) ? isPdfExtension(props.data.name) : true,
    );

    // need this for old system messages which do not contain translatedFileName
    // will be deleted that part in the future
    const computedTitle = computed(() =>
      props.toDownload
        ? props.data.translatedFileName
          ? props.data.translatedFileName
          : addLanguageToFileName(props.data.name, props.data.targetLanguage)
        : props.index + '. ' + props.data.name,
    );

    const clickTitle = () => emit('title-clicked');
    const clickButton = () => emit('button-clicked');

    const handleImageLoad = () => (isImageValid.value = true);
    const handleImageError = () => (isImageValid.value = false);

    return {
      computedTitle,
      clickButton,
      clickTitle,
      firstPageUrl,
      isImageValid,
      isTitleActive,
      handleImageError,
      handleImageLoad,
    };
  },
};
</script>

<style scoped lang="scss">
.document-card-wrapper {
  background-color: $bb-neutral-0;
  border-radius: 8px;
  padding: $bb-spacing-2;

  .document-card-wrapper-content {
    display: flex;
    flex-direction: row;
    gap: $bb-spacing-2;

    .image-container {
      display: flex;
      justify-content: center;
      border-radius: $bb-spacing-1;
      background-color: $bb-neutral-5;
      min-width: 120px;
      max-width: 120px;
      height: 120px;
      align-items: flex-start;
      gap: $bb-spacing-1;
      position: relative;

      .default-thumb {
        font-size: $bb-spacing-7;
        align-self: center;
        padding: $bb-spacing-2;
      }

      .document-thumb {
        max-width: 106px;
        max-height: 108px;
        align-self: center;
      }

      .favorite-button-container {
        position: absolute;
        right: $bb-spacing-1;
        top: $bb-spacing-1;

        button {
          border-radius: 100px;
          background: $bb-neutral-0;
          height: 34px;
          width: 38px;

          &:hover {
            :deep(.v-btn__content) {
              color: $bb-neutral-1;
            }
          }
        }
      }
    }

    .document-details {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      max-width: 100%;

      .document-title {
        @include bb-medium-text-styles('lg');
        line-height: $bb-spacing-5;
        color: $bb-neutral-1;
        cursor: pointer;
        margin-bottom: $bb-spacing-2;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        white-space: pre-wrap;
        word-break: break-word;
      }

      span {
        @include bb-regular-text-styles('xs');
        color: $bb-neutral-1;
        letter-spacing: 0.1px;
        font-size: 13px;
      }

      .bullet {
        margin: 0 $bb-spacing-1;
      }
    }
  }
}

.inactive-class {
  cursor: not-allowed !important;
}
</style>
